import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { SpecraIconButton } from '../ensigns/icon-button';
import { SpecraStatusDot } from '../ensigns/status-dot';
/**
 * The walkthrough's steps: the step numbers given on nodes and links, or,
 * when none are given, one step per node in layout order. A node without a
 * step is context (always shown); a link without one appears with the later
 * of its ends.
 */ export function flowSteps(placedIds, nodes, edges) {
    const given = [
        ...nodes.map((n)=>n.step),
        ...edges.map((e)=>e.step)
    ].filter((v)=>typeof v === 'number' && Number.isFinite(v));
    const node = new Map();
    if (given.length) {
        for (const n of nodes)node.set(n.id, n.step ?? 0);
    } else {
        placedIds.forEach((id, i)=>node.set(id, i + 1));
    }
    const edge = (e)=>e.step ?? Math.max(node.get(e.from) ?? 0, node.get(e.to) ?? 0);
    const all = new Set([
        ...node.values(),
        ...edges.map(edge)
    ]);
    all.delete(0);
    return {
        steps: [
            ...all
        ].sort((a, b)=>a - b),
        node,
        edge
    };
}
const PLAY_MS = 1600;
const NODE_W = 184;
const NODE_H = 64;
const LAYER_GAP = 64;
const NODE_GAP = 24;
/** The width a long link takes in a layer it passes through. */ const LANE = 18;
const PAD = 8;
/** How far a loop back between neighbouring layers bows out. */ const BOW = 40;
const LABEL_H = 18;
const LABEL_MAX_W = 160;
/** A label's width at the caption size, roughly. */ const labelWidth = (text)=>Math.min(LABEL_MAX_W, text.length * 6.4 + 14);
/** An SVG path through the route's points, curving along each point's direction. */ export function routePath(route) {
    const [first, ...rest] = route.points;
    let d = `M${first.p.x},${first.p.y}`;
    let prev = first;
    for (const cur of rest){
        const dx = cur.p.x - prev.p.x;
        const dy = cur.p.y - prev.p.y;
        const k0 = Math.max(Math.abs(dx * prev.dir.x + dy * prev.dir.y) / 2, prev.bow ? BOW : 0);
        const k1 = Math.max(Math.abs(dx * cur.dir.x + dy * cur.dir.y) / 2, cur.bow ? BOW : 0);
        const c1 = {
            x: prev.p.x + prev.dir.x * k0,
            y: prev.p.y + prev.dir.y * k0
        };
        const c2 = {
            x: cur.p.x - cur.dir.x * k1,
            y: cur.p.y - cur.dir.y * k1
        };
        d += ` C${c1.x},${c1.y} ${c2.x},${c2.y} ${cur.p.x},${cur.p.y}`;
        prev = cur;
    }
    return d;
}
/**
 * Layered layout (Sugiyama style). Loops back are turned round for layering
 * rather than dropped, and every link longer than one layer gets a lane in
 * each layer it crosses, so no link runs through a box. Barycenter sweeps
 * cut crossings, then each box is pulled towards its neighbours so chains
 * run straight. Deterministic for a given input.
 */ export function layoutFlow(nodes, edges, direction) {
    const tb = direction === 'TB';
    const ids = new Set(nodes.map((n)=>n.id));
    const valid = edges.map((edge, index)=>({
            edge,
            index
        })).filter(({ edge: e })=>ids.has(e.from) && ids.has(e.to) && e.from !== e.to);
    // Loops back: links that close a cycle, found by DFS in input order.
    const byFrom = new Map();
    valid.forEach(({ edge: e }, i)=>byFrom.set(e.from, [
            ...byFrom.get(e.from) ?? [],
            i
        ]));
    const state = new Map();
    const back = new Set();
    const visit = (id)=>{
        state.set(id, 1);
        for (const i of byFrom.get(id) ?? []){
            const s = state.get(valid[i].edge.to) ?? 0;
            if (s === 1) back.add(i);
            else if (s === 0) visit(valid[i].edge.to);
        }
        state.set(id, 2);
    };
    for (const n of nodes)if (!state.get(n.id)) visit(n.id);
    // Each link as it runs down the layers: a loop back is turned round.
    const ends = valid.map(({ edge: e }, i)=>back.has(i) ? [
            e.to,
            e.from
        ] : [
            e.from,
            e.to
        ]);
    // Longest-path layering.
    const parents = new Map(nodes.map((n)=>[
            n.id,
            []
        ]));
    for (const [s, t] of ends)parents.get(t)?.push(s);
    const layerOf = new Map();
    const depth = (id)=>{
        const known = layerOf.get(id);
        if (known !== undefined) return known;
        layerOf.set(id, 0);
        const ps = parents.get(id) ?? [];
        const d = ps.length ? Math.max(...ps.map(depth)) + 1 : 0;
        layerOf.set(id, d);
        return d;
    };
    for (const n of nodes)depth(n.id);
    // Rows of items: boxes, and lanes for links that skip layers.
    const layers = [];
    const addTo = (l, id)=>{
        layers[l] = [
            ...layers[l] ?? [],
            id
        ];
        layerOf.set(id, l);
    };
    for (const n of nodes)addTo(layerOf.get(n.id) ?? 0, n.id);
    const up = new Map();
    const down = new Map();
    const link = (a, b)=>{
        down.set(a, [
            ...down.get(a) ?? [],
            b
        ]);
        up.set(b, [
            ...up.get(b) ?? [],
            a
        ]);
    };
    const chains = ends.map(([s, t], i)=>{
        const chain = [
            s
        ];
        for(let l = (layerOf.get(s) ?? 0) + 1; l < (layerOf.get(t) ?? 0); l++){
            const lane = `~${i}:${l}`;
            addTo(l, lane);
            chain.push(lane);
        }
        chain.push(t);
        for(let k = 1; k < chain.length; k++)link(chain[k - 1], chain[k]);
        return chain;
    });
    for(let l = 0; l < layers.length; l++)layers[l] = layers[l] ?? [];
    const isLane = (id)=>id.startsWith('~');
    // Barycenter sweeps: down by what is above, then up by what is below.
    const order = new Map();
    const index = ()=>{
        for (const row of layers)row.forEach((id, i)=>order.set(id, i));
    };
    index();
    const bary = (id, nbrs)=>nbrs?.length ? nbrs.reduce((a, n)=>a + (order.get(n) ?? 0), 0) / nbrs.length : order.get(id) ?? 0;
    for(let pass = 0; pass < 4; pass++){
        for(let l = 1; l < layers.length; l++){
            layers[l].sort((a, b)=>bary(a, up.get(a)) - bary(b, up.get(b)));
            index();
        }
        for(let l = layers.length - 2; l >= 0; l--){
            layers[l].sort((a, b)=>bary(a, down.get(a)) - bary(b, down.get(b)));
            index();
        }
    }
    // Positions along each row: start packed, then pull each item towards the
    // centre of its neighbours, keeping order and spacing.
    const along = tb ? NODE_W : NODE_H;
    const across = tb ? NODE_H : NODE_W;
    const size = (id)=>isLane(id) ? LANE : along;
    const sep = (a, b)=>(size(a) + size(b)) / 2 + (isLane(a) || isLane(b) ? LANE / 2 : NODE_GAP);
    const at = new Map();
    for (const row of layers){
        let c = 0;
        row.forEach((id, i)=>{
            c = i === 0 ? size(id) / 2 : c + sep(row[i - 1], id);
            at.set(id, c);
        });
    }
    const settle = (row, nbrs)=>{
        if (!row.length) return;
        const want = row.map((id)=>{
            const ns = nbrs.get(id);
            return ns?.length ? ns.reduce((a, n)=>a + (at.get(n) ?? 0), 0) / ns.length : at.get(id) ?? 0;
        });
        const right = [
            ...want
        ];
        for(let i = 1; i < row.length; i++)right[i] = Math.max(right[i], right[i - 1] + sep(row[i - 1], row[i]));
        const left = [
            ...want
        ];
        for(let i = row.length - 2; i >= 0; i--)left[i] = Math.min(left[i], left[i + 1] - sep(row[i], row[i + 1]));
        row.forEach((id, i)=>at.set(id, (right[i] + left[i]) / 2));
    };
    for(let pass = 0; pass < 4; pass++){
        for(let l = 1; l < layers.length; l++)settle(layers[l], up);
        for(let l = layers.length - 2; l >= 0; l--)settle(layers[l], down);
    }
    // To x/y. "main" runs with the flow, "side" across it.
    const main = tb ? {
        x: 0,
        y: 1
    } : {
        x: 1,
        y: 0
    };
    const side = tb ? {
        x: 1,
        y: 0
    } : {
        x: 0,
        y: 1
    };
    const pt = (c, a)=>tb ? {
            x: c,
            y: a
        } : {
            x: a,
            y: c
        };
    const top = (l)=>l * (across + LAYER_GAP);
    const byId = new Map(nodes.map((n)=>[
            n.id,
            n
        ]));
    let placed = layers.flatMap((row, l)=>row.flatMap((id)=>{
            const node = byId.get(id);
            if (!node) return [];
            const corner = pt((at.get(id) ?? 0) - along / 2, top(l));
            return [
                {
                    ...node,
                    ...corner
                }
            ];
        }));
    const centre = (id)=>{
        const l = layerOf.get(id) ?? 0;
        return pt(at.get(id) ?? 0, top(l) + across / 2);
    };
    const half = {
        main: across / 2,
        side: along / 2
    };
    const scale = (v, k)=>({
            x: v.x * k,
            y: v.y * k
        });
    const plus = (a, b)=>({
            x: a.x + b.x,
            y: a.y + b.y
        });
    let routes = valid.map(({ edge, index: i }, k)=>{
        const chain = chains[k];
        // A lane's entry and exit across its layer.
        const lanes = chain.slice(1, -1).flatMap((id)=>{
            const c = centre(id);
            return [
                plus(c, scale(main, -half.main)),
                plus(c, scale(main, half.main))
            ];
        });
        if (!back.has(k)) {
            const s = plus(centre(chain[0]), scale(main, half.main));
            const t = plus(centre(chain[chain.length - 1]), scale(main, -half.main));
            return {
                index: i,
                edge,
                back: false,
                points: [
                    s,
                    ...lanes,
                    t
                ].map((p)=>({
                        p,
                        dir: main
                    }))
            };
        }
        // A loop back leaves the side of its box, climbs its lanes and enters
        // the side of the box it returns to.
        const from = centre(edge.from);
        const to = centre(edge.to);
        const sideOf = (c, toward)=>{
            const d = toward ? (toward.x - c.x) * side.x + (toward.y - c.y) * side.y : 1;
            return d < 0 ? -1 : 1;
        };
        const climb = [
            ...lanes
        ].reverse();
        const s0 = sideOf(from, climb[0]);
        const s1 = sideOf(to, climb[climb.length - 1] ?? plus(to, side));
        const rise = scale(main, -1);
        return {
            index: i,
            edge,
            back: true,
            points: [
                {
                    p: plus(from, scale(side, s0 * half.side)),
                    dir: scale(side, s0),
                    bow: true
                },
                ...climb.map((p)=>({
                        p,
                        dir: rise
                    })),
                {
                    p: plus(to, scale(side, s1 * half.side)),
                    dir: scale(side, -s1),
                    bow: true
                }
            ]
        };
    });
    const hit = (a, b)=>a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;
    const boxes = placed.map((n)=>({
            x0: n.x,
            y0: n.y,
            x1: n.x + NODE_W,
            y1: n.y + NODE_H
        }));
    const labelBox = (c, w)=>({
            x0: c.x - w / 2 - 2,
            y0: c.y - LABEL_H / 2 - 2,
            x1: c.x + w / 2 + 2,
            y1: c.y + LABEL_H / 2 + 2
        });
    routes = routes.map((r)=>{
        if (!r.edge.label) return r;
        const pts = r.points.map((q)=>q.p);
        const m = Math.max(1, Math.floor(pts.length / 2));
        let c = scale(plus(pts[m - 1], pts[m]), 0.5);
        if (r.back && pts.length === 2) c = plus(c, scale(r.points[0].dir, BOW * 0.75));
        const w = labelWidth(r.edge.label);
        const step = {
            x: 0,
            y: LABEL_H + 2
        };
        // Up and down the link first, then sideways; failing all, the spot that
        // covers the fewest others.
        const tries = [
            0,
            1,
            -1,
            2,
            -2,
            3,
            -3
        ].flatMap((t)=>[
                0,
                w / 2 + 8,
                -(w / 2 + 8)
            ].map((sx)=>plus(plus(c, scale(step, t)), {
                    x: sx,
                    y: 0
                })));
        const clashes = (p)=>boxes.filter((b)=>hit(b, labelBox(p, w))).length;
        const spot = tries.find((p)=>clashes(p) === 0) ?? tries.reduce((best, p)=>clashes(p) < clashes(best) ? p : best, c);
        boxes.push(labelBox(spot, w));
        return {
            ...r,
            label: spot
        };
    });
    // Fit everything (loops bow out past the boxes) with PAD all round.
    const xs = [];
    const ys = [];
    for (const b of boxes){
        xs.push(b.x0, b.x1);
        ys.push(b.y0, b.y1);
    }
    for (const r of routes){
        for (const q of r.points){
            // A bowed anchor swings out about 3/4 of BOW; leave room for all of it.
            const reach = q.bow ? [
                plus(q.p, scale(q.dir, BOW)),
                plus(q.p, scale(q.dir, -BOW))
            ] : [
                q.p
            ];
            for (const p of reach){
                xs.push(p.x);
                ys.push(p.y);
            }
        }
    }
    const dx = PAD - Math.min(...xs);
    const dy = PAD - Math.min(...ys);
    const move = (p)=>({
            x: p.x + dx,
            y: p.y + dy
        });
    placed = placed.map((n)=>({
            ...n,
            ...move(n)
        }));
    routes = routes.map((r)=>({
            ...r,
            points: r.points.map((q)=>({
                    ...q,
                    p: move(q.p)
                })),
            label: r.label && move(r.label)
        }));
    return {
        placed,
        routes,
        width: Math.max(...xs) + dx + PAD,
        height: Math.max(...ys) + dy + PAD
    };
}
/** Process flows, hierarchies and org charts drawn with the design system. */ export function SpecraFlowDiagram({ nodes, edges, direction = 'TB', title, label, walkthrough = false, animated = false, className, ...props }) {
    const markerId = React.useId();
    const { placed, routes, width, height } = React.useMemo(()=>layoutFlow(nodes, edges, direction), [
        nodes,
        edges,
        direction
    ]);
    const plan = React.useMemo(()=>flowSteps(placed.map((p)=>p.id), nodes, edges), [
        placed,
        nodes,
        edges
    ]);
    const [stepIndex, setStepIndex] = React.useState(0);
    const [playing, setPlaying] = React.useState(false);
    const last = plan.steps.length - 1;
    React.useEffect(()=>{
        if (!playing) return;
        if (stepIndex >= last) {
            setPlaying(false);
            return;
        }
        const t = setTimeout(()=>setStepIndex((i)=>Math.min(last, i + 1)), PLAY_MS);
        return ()=>clearTimeout(t);
    }, [
        playing,
        stepIndex,
        last
    ]);
    if (placed.length === 0) return null;
    const stepping = walkthrough && plan.steps.length > 1;
    const current = plan.steps[Math.min(stepIndex, last)] ?? 0;
    // 'past' full strength, 'now' emphasised, 'later' faded; no walkthrough: all past.
    const phase = (step)=>!stepping || step < current ? 'past' : step === current ? 'now' : 'later';
    const go = (i)=>{
        setPlaying(false);
        setStepIndex(Math.max(0, Math.min(last, i)));
    };
    const nowNodes = placed.filter((n)=>(plan.node.get(n.id) ?? 0) === current);
    const nowEdge = edges.find((e)=>plan.edge(e) === current && e.label);
    const caption = nowNodes.length ? `${nowNodes.map((n)=>n.label).join(' · ')}${nowNodes[0].detail ? `: ${nowNodes[0].detail}` : ''}` : nowEdge?.label ?? '';
    const onKeyDown = (e)=>{
        if (!stepping) return;
        if (e.key === 'ArrowRight') {
            e.preventDefault();
            go(stepIndex + 1);
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            go(stepIndex - 1);
        }
    };
    const paths = routes.map((r)=>({
            key: `${r.index}:${r.edge.from}->${r.edge.to}`,
            d: routePath(r),
            e: r.edge,
            label: r.label,
            phase: phase(plan.edge(r.edge))
        }));
    return /*#__PURE__*/ _jsxs("figure", {
        className: cn('m-0', stepping && 'rounded-md outline-none', className),
        "aria-label": label,
        "data-walkthrough": stepping ? '' : undefined,
        onKeyDown: stepping ? onKeyDown : undefined,
        tabIndex: stepping ? 0 : undefined,
        ...props,
        children: [
            title && /*#__PURE__*/ _jsx("figcaption", {
                className: "mb-2 text-[length:var(--fs-ui-caption)] font-medium text-foreground",
                children: title
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-x-auto",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "relative",
                    style: {
                        width,
                        height
                    },
                    children: [
                        /*#__PURE__*/ _jsxs("svg", {
                            className: "absolute inset-0 text-muted-foreground",
                            width: width,
                            height: height,
                            children: [
                                /*#__PURE__*/ _jsx("title", {
                                    children: `${label}: connections`
                                }),
                                /*#__PURE__*/ _jsx("defs", {
                                    children: /*#__PURE__*/ _jsx("marker", {
                                        id: markerId,
                                        viewBox: "0 0 8 8",
                                        refX: "7",
                                        refY: "4",
                                        markerWidth: "7",
                                        markerHeight: "7",
                                        orient: "auto-start-reverse",
                                        children: /*#__PURE__*/ _jsx("path", {
                                            d: "M0,0 L8,4 L0,8 z",
                                            fill: "currentColor"
                                        })
                                    })
                                }),
                                paths.map((p)=>/*#__PURE__*/ _jsx("path", {
                                        d: p.d,
                                        fill: "none",
                                        stroke: "currentColor",
                                        className: cn(animated && 'specra-flow-march', p.phase === 'later' && 'print:[stroke-opacity:0.55]'),
                                        "data-step-phase": stepping ? p.phase : undefined,
                                        strokeOpacity: p.phase === 'now' ? 0.95 : p.phase === 'later' ? 0.28 : 0.6,
                                        strokeWidth: p.phase === 'now' ? 2 : 1.25,
                                        strokeDasharray: p.e.dashed ? '4 3' : undefined,
                                        markerEnd: `url(#${markerId})`
                                    }, p.key))
                            ]
                        }),
                        paths.map((p)=>p.e.label && p.label ? /*#__PURE__*/ _jsx("span", {
                                className: cn('absolute -translate-x-1/2 -translate-y-1/2 truncate whitespace-nowrap rounded-full border border-border bg-background px-1.5 text-[length:var(--fs-ui-caption)] leading-4 text-muted-foreground transition-opacity duration-[var(--dur-fast)]', p.phase === 'later' && 'opacity-40 print:opacity-100'),
                                style: {
                                    left: p.label.x,
                                    top: p.label.y,
                                    maxWidth: LABEL_MAX_W
                                },
                                title: p.e.label,
                                children: p.e.label
                            }, `${p.key}-label`) : null),
                        placed.map((n)=>{
                            const ph = phase(plan.node.get(n.id) ?? 0);
                            return /*#__PURE__*/ _jsxs("div", {
                                "data-step-phase": stepping ? ph : undefined,
                                className: cn('absolute flex flex-col justify-center rounded-md border border-border bg-card px-2.5 shadow-sm transition-opacity duration-[var(--dur-fast)]', ph === 'now' && 'ring-2 ring-ring', ph === 'later' && 'opacity-45 print:opacity-100'),
                                style: {
                                    left: n.x,
                                    top: n.y,
                                    width: NODE_W,
                                    height: NODE_H
                                },
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-baseline gap-1.5 text-[length:var(--fs-ui-caption)] font-medium leading-4 text-foreground",
                                        children: [
                                            n.tone && /*#__PURE__*/ _jsx(SpecraStatusDot, {
                                                tone: n.tone
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "line-clamp-2",
                                                title: n.label,
                                                children: n.label
                                            })
                                        ]
                                    }),
                                    n.detail && /*#__PURE__*/ _jsx("div", {
                                        className: "truncate text-[length:var(--fs-ui-caption)] leading-4 text-muted-foreground",
                                        title: n.detail,
                                        children: n.detail
                                    })
                                ]
                            }, n.id);
                        })
                    ]
                })
            }),
            stepping && /*#__PURE__*/ _jsxs("div", {
                className: "mt-2 flex items-center gap-1 print:hidden",
                "data-print-hide": "",
                children: [
                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                        disabled: stepIndex === 0,
                        label: "Previous step",
                        onClick: ()=>go(stepIndex - 1),
                        size: "sm",
                        variant: "ghost",
                        children: /*#__PURE__*/ _jsx(ChevronLeft, {
                            "aria-hidden": "true"
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                        label: playing ? 'Pause' : 'Play',
                        onClick: ()=>{
                            if (playing) setPlaying(false);
                            else {
                                if (stepIndex >= last) setStepIndex(0);
                                setPlaying(true);
                            }
                        },
                        size: "sm",
                        variant: "ghost",
                        children: playing ? /*#__PURE__*/ _jsx(Pause, {
                            "aria-hidden": "true"
                        }) : /*#__PURE__*/ _jsx(Play, {
                            "aria-hidden": "true"
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                        disabled: stepIndex >= last,
                        label: "Next step",
                        onClick: ()=>go(stepIndex + 1),
                        size: "sm",
                        variant: "ghost",
                        children: /*#__PURE__*/ _jsx(ChevronRight, {
                            "aria-hidden": "true"
                        })
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "shrink-0 text-[length:var(--fs-ui-caption)] tabular-nums text-muted-foreground",
                        children: [
                            "Step ",
                            stepIndex + 1,
                            " of ",
                            plan.steps.length
                        ]
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        "aria-live": "polite",
                        className: "min-w-0 truncate text-[length:var(--fs-ui-caption)] text-foreground",
                        "data-walkthrough-caption": "",
                        children: caption
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "sr-only",
                children: placed.map((n)=>/*#__PURE__*/ _jsxs("li", {
                        children: [
                            n.label,
                            n.detail ? `: ${n.detail}` : ''
                        ]
                    }, n.id))
            })
        ]
    });
}
